/**
 * Changelog data — the single source of truth for the user-facing changelog
 * surfaced at /help/changelog.
 *
 * ─── CONVENTIONS ─────────────────────────────────────────────────────────────
 * • Newest entry FIRST (top of the array).
 * • Each entry MUST have: date (YYYY-MM-DD), category, title, summary.
 * • Optional fields: where (breadcrumb to the affected UI), details (bullet list).
 * • `category` is one of: 'Feature' | 'Enhancement' | 'Bug Fix' | 'UI' | 'Backend'.
 *   Feature   — brand-new capability
 *   Enhancement — improves an existing capability
 *   Bug Fix   — corrects incorrect behaviour
 *   UI        — cosmetic / layout only, no logic change
 *   Backend   — visible only through indirect effects (perf, reliability)
 *
 * ─── HOW TO APPEND A NEW ENTRY ──────────────────────────────────────────────
 * Add a new object at the TOP of the array with today's date. Keep the summary
 * to one short sentence a non-technical user can understand. Move
 * implementation detail into `details[]` bullets. This file is the *user's*
 * changelog — the developer-facing one lives at /app/memory/CHANGELOG.md.
 */
export const CHANGELOG_ENTRIES = [
  {
    date: '2026-07-08',
    category: 'Feature',
    title: 'P&L drill-in — click any category to see its transactions',
    summary: 'Every leaf row on the Profit & Loss statement is now clickable — click Postage / Freight / Stock Purchases / etc. to expand a mini transactions table right there. Includes a "→ Open in Expenses/Purchases page" link for the full toolkit.',
    where: 'Reports → Profit & Loss',
    details: [
      'Click any leaf category (▶ turns to ▼) → inline table opens below showing Date · Description · Supplier · Payment Method · Total for each transaction.',
      'Works for BOTH Operating Expenses (Freight → Postage, Software → Xero, etc.) AND Cost of Sales (Stock Purchases → individual purchase invoices).',
      'Each drill respects the current date range filter — Last 7 Days shows only that week\'s transactions.',
      'Cached on click — clicking the same row again just toggles the panel without a re-fetch.',
      '"→ Open in Expenses page" / "→ Open in Purchases page" link at the bottom of each drill takes you to the full editing toolkit (Reconciled checkbox, Payment Methods Breakdown, etc.) with filters ready.',
      'Backend: new `GET /api/pnl/category-transactions?source=expense|purchase&category=X&category_code=Y&start_date=...&end_date=...` endpoint returns the transaction list.',
      'Sorted most-recent first. Limits to 500 rows per drill to keep the UI snappy.',
    ],
  },
  {
    date: '2026-07-08',
    category: 'Bug Fix',
    title: 'Payment Status report now matches P&L — one source of truth for "Paid"',
    summary: 'The "Paid" figure on Payment Status was reading a stale `payment_status` field that wasn\'t kept in sync with reality. It now derives from `revenue_date` — the same field the P&L uses. Numbers match everywhere.',
    where: 'Reports → Payment Status · Reports → Financial · Dashboard → Awaiting Payment card',
    details: [
      'Root cause: legacy `payment_status="Paid"` field wasn\'t reliably set on all closed jobs (Jim2-migrated data + inconsistent write paths). So P&L said $11,559 while Payment Status said $730 for the same window — same jobs, different fields consulted.',
      'Fix: three payment-state buckets are now DERIVED, not stored:',
      '  • 💰 **Paid** — `revenue_date` is set (job has been paid and closed)',
      '  • ⏳ **Awaiting Payment** — status is Finish / Not Recoverable / Not Proceeding, no revenue_date yet, balance owing > 0 (the "on my shelf" jobs to chase)',
      '  • 🔧 **In Progress** — everything else (Booked, WIP, Approved, Wait, etc.)',
      'All 4 places that used the old logic switched to the derived version: Reports Payment Status, Reports Financial "Finished Revenue (Paid)" cards, Reports drill-in for payment status filter, Dashboard "Awaiting Payment" KPI card.',
      'Dashboard "Awaiting Payment" card + tooltip updated to match: "Jobs done and on your shelf waiting for the customer to collect and pay." Click it to see the chase list.',
      'Job Detail page shows the new derived state as a pill badge with tooltip (💰 Paid / ⏳ Awaiting Payment / 🔧 In Progress).',
      'No data migration needed — every job already has correct revenue_date. Only the queries changed.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Feature',
    title: 'Type-to-sign — simpler customer signature flow (email + kiosk)',
    summary: 'Customers no longer have to draw a signature with mouse or finger — a common source of frustration and timeouts. They now tick "I agree to T&Cs", type their full name, see it rendered live in cursive, and click "Accept & Sign". Faster, works identically on every device, legally equivalent under the Electronic Transactions Act 1999 (Cth).',
    where: 'Email signature link · iPad Kiosk',
    details: [
      'Customer opens the signature link (from your email or the iPad kiosk).',
      'Reviews job details + Terms & Conditions, ticks the agreement checkbox.',
      'Types their full name into the input field.',
      'Their name renders LIVE in a cursive font (looks like a signature).',
      'Clicks "✓ Accept & Sign" — done.',
      'Behind the scenes: the rendered cursive image is stored as a PNG (identical to the old drawn signature format — printed job cards look the same).',
      'Full audit metadata captured server-side: typed name, IP address, browser/device, timestamp, and signature type = "typed". Legally enforceable + tamper-evident.',
      'Existing signed jobs are unchanged — this only applies to jobs signed going forward.',
      'The email link no longer has a "Sign Now" button that opens a canvas — the pad is inline immediately, so customers see one screen and know what to do.',
      'iPad Kiosk uses the same UI (aligned experience whether they sign at the counter or later at home).',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Enhancement',
    title: 'Referral Partners now drill-in like By Location (consistent report UX)',
    summary: 'Clicking a partner row (e.g. Drivesavers) now opens the same drill-in card as the By Location tab — Workflow Health bar, Status Breakdown chips, and a sortable list of the jobs they referred, each with a View button to open the job card.',
    where: 'Reports → Referral Partners',
    details: [
      'Click any partner row → the drill card appears below with just their jobs. Row highlights blue while drilled in.',
      'Workflow Health bar shows the status mix (Approved / Booked / Finished / etc.) as coloured segments.',
      'Status Breakdown chips let you further narrow to a single status (e.g. "show only their Approved jobs").',
      'Sortable jobs table with columns: Job #, Customer, Type, Status, Date In, Amount, plus a View button that opens the job card.',
      'Clear Filter button resets the drill and returns to the partners overview.',
      'Backend enhancement: `/api/jobs/list-paginated` now includes `referred_by_partner_id` and `referred_by_partner_name` in its projection — was silently omitting them, which is why the drill showed 0 jobs initially.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Feature',
    title: 'Reconciled checkbox column on Expenses & Purchases (resumable statement reconciliation)',
    summary: 'A new checkbox column lets you tick off each expense / purchase as you match it against your bank / credit-card / PayPal statement. Ticked rows highlight green + fade, and a "Show unreconciled only" toggle hides completed rows so you can focus on what\'s left. State is saved to the database, so you can close the tab and pick up exactly where you left off.',
    where: 'Expenses · Purchases',
    details: [
      'New Reconciled column with a green checkbox at the start of each row on both Expenses and Purchases tables.',
      'Ticked rows highlight in soft green + slightly faded, so at a glance you see what\'s done.',
      'Hovering the checkbox shows a tooltip with the reconciled date + who ticked it — audit-friendly.',
      'New "☐ Show unreconciled only" button in the filter bar. Click to hide reconciled rows; button flips to "☑ Showing unreconciled only" so you know the filter is active.',
      'Progress counter next to the toggle: "2 / 47 reconciled (4%)" — updates live as you tick rows.',
      'When you\'ve reconciled everything, the empty state reads "All X matching expense(s) reconciled ✓ — untick to see them again."',
      'Optimistic UI: the checkbox flips instantly; if the save fails, it rolls back and shows an error.',
      'Persists to the database (`reconciled`, `reconciled_at`, `reconciled_by` fields on Expense and Purchase models). Close the tab, come back next month, resume exactly where you left off.',
      'Perfect stacking with all other filters: date range + Amex breakdown row + "Show unreconciled only" → gives you the exact list of Amex charges still to check against this month\'s statement.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Feature',
    title: 'Click a payment method → transactions filter (reconciliation mode)',
    summary: 'On both Expenses and Purchases, clicking a row in the 💳 Payment Methods Breakdown now instantly filters the transactions table below to just that method — one-click reconciliation against a bank / credit card / PayPal statement.',
    where: 'Expenses · Purchases',
    details: [
      'Click "American Express" → transactions table shows ONLY American Express expenses. Click it again to clear.',
      'Active row is highlighted blue; a small "Filtered: American Express ✕" chip appears in the card header — click the ✕ to clear.',
      'Works alongside all your other filters (date range, category, supplier, business/internal) — they stack.',
      'Perfect end-of-month workflow: filter to This Month → click "American Express" → cross-check line by line against your Amex statement → mark off / correct any discrepancies without leaving the page.',
      'Same treatment on the Purchases page — reconcile Bank Transfer, PayPal, Card, etc. against their respective statements.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'UI',
    title: 'Info tooltip on "Payment date vs Booking date" toggle',
    summary: 'The Payment Status tab\'s date-basis toggle now has an ℹ️ tooltip explaining what each mode does and clarifies that it ONLY affects the Payment Status tab.',
    where: 'Reports → Payment Status → toggle',
    details: [
      'Hover the info icon to see side-by-side plain-English explanations of "💰 Payment date" (cash-basis) vs "📅 Booking date" (activity view).',
      'Reinforces the scope: "This toggle only affects the Payment Status tab. Other tabs always use Booking date."',
      'Prevents future confusion about missing job counts on Job Distribution / By Location / etc.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Bug Fix',
    title: 'Job Distribution counts were being narrowed by the Payment Status cash-basis toggle',
    summary: 'When you were on Reports → Job Distribution (or By Location / Priority / Referrals) for a recent date range, you were seeing ONLY paid jobs because the "Payment date vs Booking date" toggle from the Payment Status tab was silently applied to every tab. Now each tab correctly filters by booking date, except Payment Status which still respects the toggle.',
    where: 'Reports → Job Distribution · By Location · Referrals · Priority · Customer Types',
    details: [
      'Root cause: `paymentDateField` state defaulted to `revenue` (cash-basis) and was applied globally to `/reports/distribution`, hiding unpaid jobs on every non-Payment-Status tab.',
      'Fix: Distribution now uses `date_in` (booking date) EVERYWHERE except when the user is actively on the Payment Status tab AND has toggled to Payment date.',
      'Example: 1-7 July on Preview previously showed 8 jobs (paid only); now correctly shows 18 (all activity in the week).',
      'Payment Status tab unchanged — still defaults to Payment date (cash-basis) with toggle to Booking date.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Bug Fix',
    title: 'Referral Partners report now shows newly-booked partner referrals (before payment)',
    summary: 'The Referral Partners tab was filtering by PAID date, so a partner who sent you a job that hasn\'t been paid yet would vanish from the report. Now filters by BOOKING date — you see referral activity in real time.',
    where: 'Reports → Referral Partners',
    details: [
      'Backend `/reports/referral-partners` now filters by `date_in` (booking date), not `revenue_date` (paid date).',
      'Result: a partner-referred job booked this week shows up immediately with jobs_count=1 and $0 revenue (correct — you haven\'t received the money yet).',
      'Revenue column still uses cash-basis (sums `revenue_amount`), so once the job is paid the $ appears — no double-counting.',
      'Updated card description makes the behaviour explicit: "filtered by booking date, revenue is cash-basis".',
      'Also improved error handling on Reports: "Failed to load…" toasts now include the actual reason (HTTP code / timeout / network error) so we can triage the intermittent failures you reported.',
      'Added a 30s timeout to Job Distribution fetch + 45s timeout to the heavy 17k-job fetch so a stalled request no longer hangs the UI silently.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Feature',
    title: 'Payment Methods Breakdown on Expenses & Purchases + editable Quick Entry Templates',
    summary: 'See at a glance how much you spent by payment method (Amex vs Card vs Bank Transfer vs …) for any date range — plus manage the one-click "Quick Entry" chips yourself in Settings.',
    where: 'Expenses · Purchases · Settings → Dropdown Values',
    details: [
      '💳 New "Payment Methods Breakdown" card on the Expenses page — grouped by method with # expenses, ex-GST, inc-GST, and % of total. Respects your active date/category/supplier filters.',
      '💳 Same breakdown card added to Purchases — respects date + supplier + internal/business filters.',
      '⚡ Purchases page gains its own Quick Entry chips (mirror of the Expenses one) — one-click pre-fills a Record Purchase with a saved supplier + payment method.',
      '🛠 New "Expense Quick Entry Templates" section in Settings → Dropdown Values — Add / delete chips yourself (Button Label, Category, Description, Payment Method).',
      '🛠 New "Purchase Quick Entry Templates" section — same idea for Purchases (Button Label, Supplier name, Payment Method, optional Notes).',
      'Purchase Quick Entry auto-resolves the Supplier by name — if it matches an existing supplier, the form pre-fills instantly.',
      'All template lists use the SAME live Payment Methods dropdown from Settings — anything you add there (e.g. American Express) is instantly selectable in the templates too.',
    ],
  },
  {
    date: '2026-07-07',
    category: 'Enhancement',
    title: 'Purchases payment methods now editable (add American Express, PayPal, etc.)',
    summary: 'The Payment Method dropdown on the Purchases form was hardcoded — you couldn\'t add "American Express" without a code change. Now it reads from the same editable list as Expenses.',
    where: 'Purchases → Record Purchase · Settings → Dropdown Values → Payment Methods',
    details: [
      'Add / remove / reorder payment methods in Settings → Dropdown Values → Payment Methods (same list Expenses already uses).',
      'Any method you add there instantly appears on both the Expenses and Purchases forms.',
      'The old hardcoded list (Cash, Card, Bank Transfer, Direct Debit, Credit Card, Other) is still the fallback if settings are empty.',
      'Small "Add new payment methods (e.g. American Express) in Settings…" hint under the dropdown so you always know where to find it.',
    ],
  },
  {
    date: '2026-07-06',
    category: 'Feature',
    title: 'Brevo unsubscribe webhook — automatic opt-out sync',
    summary: 'When customers click "unsubscribe" in a Brevo email, they\'re now instantly flagged in this app so they\'re excluded from all future newsletter syncs. Also handles hard bounces and spam complaints.',
    where: 'Newsletter → Settings → Unsubscribe Sync (Webhook)',
    details: [
      'New webhook endpoint POST /api/webhooks/brevo that Brevo POSTs to on unsubscribe / hard_bounce / spam events.',
      'Matching customer is instantly marked newsletter_unsubscribed=true (with reason + timestamp) and their consent is revoked.',
      'Optional signing secret (Generate → paste into Brevo) prevents anyone else from spoofing fake unsubscribes.',
      'Recent Events audit log — see the last 25 events with email, event type, and whether it triggered an opt-out.',
      'Setup guide with direct links to the right pages in the Brevo dashboard.',
      'Zero maintenance: once configured, opt-outs sync automatically — no more manual "Mark UNSUBSCRIBED" for customers who used Brevo\'s built-in unsubscribe link.',
    ],
  },
  {
    date: '2026-07-06',
    category: 'Feature',
    title: 'Newsletter consent tracking (Spam Act compliant)',
    summary: 'Every customer now has an explicit newsletter consent flag with source + timestamp. Sync to Brevo only includes consented customers who haven\'t unsubscribed.',
    where: 'Add Job form · Customers tab · Reports → Customer Types',
    details: [
      'Job intake form: 📮 "Add customer to newsletter list" checkbox (below Customer Type). Ticking stamps consent with source="job_form" and today\'s date.',
      'Existing customers auto-pre-tick when they\'re already consented (unless previously unsubscribed).',
      'Customers tab: new "📮 Newsletter" column with 3 states — 🚫 Unsubscribed (permanent) · ✓ Consented (click for options) · + Opt in (grey pill).',
      'Clicking a Consented pill offers "Mark UNSUBSCRIBED" — permanent flag, respects the opt-out forever.',
      'Newsletter sync endpoint now defaults to consented-only + never returns unsubscribed customers. Pass `include_pending=true` to see non-consented contacts for review.',
      'Consent audit trail preserved: newsletter_consent_at, newsletter_consent_source ("job_form"/"manual"/"import"), newsletter_unsubscribed_at.',
    ],
  },
  {
    date: '2026-07-06',
    category: 'Bug Fix',
    title: 'Customers tab date filter now works',
    summary: 'The Last 7 Days / Quarter / Custom Range filter was only shrinking job stats but showing all 13,620 customers. Now it correctly filters to customers CREATED in the selected period.',
    where: 'Customers tab',
    details: [
      'Backend /customers/with-insights start_date / end_date now filter by customer.created_at',
      'Any date range preset works — Today, Last 7 Days, This Quarter, Custom Range, etc.',
      'Combines cleanly with Customer Type filter to answer "how many new End Users this month?"',
    ],
  },
  {
    date: '2026-07-06',
    category: 'Feature',
    title: 'Customer Types report: "Customers in period" column + Copy Emails',
    summary: 'Newsletter workflow gets a huge lift. You can now see how many End Users / Partners had jobs in a specific date range, and one-click copy their email addresses.',
    where: 'Reports → Customer Types',
    details: [
      'New "📅 Customers (period)" column next to the existing "🌐 Customers (all-time)" — shows unique customers who had a job in the selected date range',
      'Drill-in modal (click any row) now gets a checkbox: "Show only X customers who had jobs in the selected date range" — defaults ON when opened from Customer Types',
      'New 📋 Copy Emails button in the drill-in modal — copies the visible customers\' emails (deduplicated, comma-separated) to clipboard, ready to paste into your newsletter tool',
      'Column headers now have visual scope indicators (🌐 lifetime · 📅 period) so mixed-scope tables are legible at a glance',
    ],
  },
  {
    date: '2026-07-06',
    category: 'Bug Fix',
    title: 'Payment Status report now aligns with the rest of the app',
    summary: 'Payment Overview and Jobs by Payment Status were filtering by BOOKING date while every other revenue report (P&L, BAS, Dashboard, Payment Methods) uses PAID date. Now aligned — with a toggle for the old behaviour.',
    where: 'Reports → Payment Status',
    details: [
      'New toggle chip at the top of the Payment Status tab: 💰 Payment date (default, cash-basis) vs 📅 Booking date (activity volume)',
      'Default is Payment date — numbers now match P&L, BAS, Dashboard Revenue and Payment Methods Breakdown',
      'Switch to Booking date any time you want the "jobs booked this period regardless of payment" view',
      'Backend `/reports/distribution` gains a `date_field` param (`date_in` | `revenue_date`)',
    ],
  },
  {
    date: '2026-07-03',
    category: 'Enhancement',
    title: 'Progress Status now visible on Dashboard jobs table',
    summary: 'The main jobs table on the Dashboard gains a Progress column right after Status, with color-matched pills for Imaging / WIP / Awaiting Parts.',
    where: 'Dashboard → Jobs Table',
    details: [
      'Colors match the Dashboard operational tiles above: sky-blue Imaging (💾), amber WIP (🛠), rose Awaiting Parts (📦)',
      '"Not Started" jobs show a subtle grey dash — keeps the eye focused on jobs actively on the bench',
      'Full jobs list only (not the small Recent Jobs sidecar) — the sidecar already surfaces the pill inline when set',
    ],
  },
  {
    date: '2026-07-03',
    category: 'UI',
    title: 'Working area widened for large monitors',
    summary: 'The main content area now stretches up to 1600px (was 1400px) so job tables and dashboards breathe on 24"+ monitors. No visible change on laptops.',
    where: 'Every page',
  },
  {
    date: '2026-07-03',
    category: 'Enhancement',
    title: 'Record Payment now defaults to Card instead of Cash',
    summary: 'The payment method dropdown defaults to "Card" — matches the most common real-world method. Cash is still one dropdown click away.',
    where: 'Job Card → Record Payment',
  },
  {
    date: '2026-07-03',
    category: 'Bug Fix',
    title: 'Payment Methods Breakdown now respects the report date range',
    summary: 'The Money-In side of Payment Methods Breakdown was showing LIFETIME totals regardless of the date filter — explaining why $5,507 / 12 payments appeared while "Jobs by Payment Status" showed $2,520 / 7 jobs for the same period.',
    where: 'Reports → Payment Status → Payment Methods Breakdown',
    details: [
      'Backend `/api/payment-methods-report` now accepts optional start_date / end_date and filters by payment_date',
      'Frontend passes the top-of-page date range to both Money-In and Money-Out sides — they used to disagree',
      'Card counts payment RECORDS while sibling counts JOBS — a job with 2 payments still counts as 1 Paid job but 2 payment records. Footnote updated to explain this',
    ],
  },
  {
    date: '2026-07-03',
    category: 'Enhancement',
    title: 'CC/BCC on Customer Email Templates + Job Card send dialog',
    summary: 'Each Customer Email Template can now carry a Default CC and Default BCC. The Job Card "Email Customer" dialog also gains CC/BCC inputs that auto-fill from the picked template.',
    where: 'Settings → Email Templates · Job Card → Email Customer',
    details: [
      'CC/BCC are comma-separated lists — put multiple addresses in either field',
      'Picking a template on the Job Card auto-fills CC/BCC from its defaults; you can still edit or clear before sending',
      'BCC is hidden from the customer (not added to email headers) but still receives the email',
      'Actual CC/BCC used on each send is now stored in the Email History entry for audit',
    ],
  },
  {
    date: '2026-07-03',
    category: 'Bug Fix',
    title: 'Dashboard Revenue now matches your credit card slips',
    summary: 'The Revenue Overview card was showing ex-GST subtotals — inconsistent with the rest of the app. Now shows inc-GST cash-basis revenue that matches every payment slip.',
    where: 'Dashboard → Revenue Overview',
    details: [
      'Numbers will jump ~10% upward — this is correct; you are now seeing the actual money received, not the ex-GST portion',
      'Filter is now `revenue_date` (day payment received) instead of `date_finished` (day work completed) — matches P&L / BAS',
      'Clicking a specific day\'s bar now drills into ONLY that day\'s jobs (previously showed the whole 7- or 30-day period)',
      'Drill-down modal now leads with "Total (inc GST)" as the primary column; ex-GST subtotal shown greyed for reference',
      'Column "Finished" renamed to "Paid on" — reflects the cash-basis date the total was recognised',
    ],
  },
  {
    date: '2026-07-03',
    category: 'Feature',
    title: 'Changelog page',
    summary: 'Every change we ship now shows up on this page — searchable, filterable, newest first.',
    where: 'Help → Changelog',
    details: [
      'Filter by category (Feature / Enhancement / Bug Fix / UI / Backend)',
      'Search across title, summary and details',
      'Newest entry always at the top',
    ],
  },
  {
    date: '2026-07-03',
    category: 'Enhancement',
    title: 'Finished jobs auto-clear their Progress Status',
    summary: 'Marking a job as Finish, Not Recoverable, or Not Proceeding now automatically resets Progress Status to "Not Started".',
    where: 'Job Card → Status',
    details: [
      'Finished jobs disappear from the Dashboard Imaging / WIP / Awaiting Parts tiles the moment you mark them done',
      'Applies to all three terminal statuses: Finish, Not Recoverable, Not Proceeding',
      'Enforced at the backend — works regardless of how the status is changed (Quick Edit, Edit form, API)',
    ],
  },
  {
    date: '2026-07-03',
    category: 'Feature',
    title: 'Edit Payment',
    summary: 'Fix a payment that was recorded with the wrong method or amount — no more delete-and-re-add.',
    where: 'Job Card → Payment History → Edit',
    details: [
      'Change amount, date, payment method, reference number, or notes',
      'Totals (Amount Paid / Balance Owing / Payment Status) recalculate automatically',
      'Activity Log captures the change in plain English (e.g. "amount $50.00 → $75.00, method Cash → Credit Card")',
      'Never retroactively re-opens a closed job (accountant-safe)',
    ],
  },
  {
    date: '2026-07-02',
    category: 'UI',
    title: 'Print Sticker inline with invoice actions',
    summary: 'The Print Sticker button now appears in the invoice action row so you don\'t have to scroll back up after emailing an invoice.',
    where: 'Job Card → Invoice section',
    details: [
      'Row order: Save | View Invoice | Print | PDF | Email | 🏷️ Print Sticker | Regenerate',
      'Uses the same printer settings as the top-of-page Print Sticker button',
    ],
  },
  {
    date: '2026-07-02',
    category: 'Feature',
    title: 'Customer Email Templates on the Job Card',
    summary: 'Send a generic, templated email to a customer straight from the Job Card — no need to build an invoice first.',
    where: 'Job Card → 📧 Email Customer',
    details: [
      '4 seeded default templates: Additional Info Required, Job Update, Job On Hold — Awaiting Reply, Quote Follow-up',
      'Placeholders: {customer_name}, {job_number}, {device_info}, {business_name}, {business_phone}, {business_email}',
      'Subject is auto-prefixed with [Job #XXXX] so customer inboxes stay scannable',
      'Full CRUD available at Settings → Email Templates → Customer Email Templates',
      'Sent emails logged in the job\'s Email History card (with full HTML body)',
    ],
  },
  {
    date: '2026-07-02',
    category: 'Enhancement',
    title: 'Auto-backup email notifications',
    summary: 'You now get an email every time the automatic backup runs — success or failure — so you know your data is safe.',
    where: 'Settings → Backup → Email backup notifications',
    details: [
      'Enable via a checkbox in Settings → Backup',
      'Success email confirms backup ran + destination (local / Google Drive)',
      'Failure email surfaces the actual error message so you can act on it',
    ],
  },
  {
    date: '2026-07-02',
    category: 'Enhancement',
    title: '"This Quarter" is now the default Reports date range',
    summary: 'Reports open on the current fiscal quarter by default — matches how you actually think about the business.',
    where: 'Reports (all tabs)',
  },
  {
    date: '2026-07-01',
    category: 'Bug Fix',
    title: 'Backup reliability improvements',
    summary: 'Fixed local browser download failures and a timezone bug that was skipping the nightly backup.',
    where: 'Settings → Backup',
    details: [
      'Local Blob download bypass — reliable across browsers and Cloudflare',
      'Auto-backup scheduler now pinned to Australia/Melbourne (was running in UTC)',
      'Startup catch-up job — if a scheduled backup was missed while the server was down, it runs at boot',
    ],
  },
  {
    date: '2026-07-01',
    category: 'Feature',
    title: 'Payment Methods report — Money-In / Money-Out toggle',
    summary: 'The Payment Methods Breakdown card now toggles between customer payments received and purchases + expenses paid.',
    where: 'Reports → Payment Status',
  },
  {
    date: '2026-07-01',
    category: 'Enhancement',
    title: 'Payment Method dropdown on the Purchases form',
    summary: 'Purchases now capture Payment Method (Cash / Card / Bank Transfer / etc.) — same vocabulary as Expenses.',
    where: 'Purchases → Add / Edit Purchase',
  },
  {
    date: '2026-07-01',
    category: 'Enhancement',
    title: 'Referral Partners — dedicated tracking + reports',
    summary: 'Partners (referrers) are now a first-class entity with their own report, distinct from the coarse "referral source" channel.',
    where: 'Add Job / Reports → Referral Partners',
    details: [
      'Typeahead field on Add / Edit Job with inline "+ Add" partner creation',
      'Managed in Settings → Dropdown Values → Referral Partners',
      'New Reports tab: Partner | Jobs Count | Total Revenue | Total Billed | First / Last Referral',
      'Partner Discount % per-job — RRP preserved on invoices, discount broken out as its own line',
    ],
  },
];

/**
 * Category → badge colour mapping. Kept in this file so it lives with the
 * category enum itself.
 */
export const CATEGORY_STYLES = {
  Feature:     { bg: '#dcfce7', fg: '#166534', border: '#86efac' },
  Enhancement: { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd' },
  'Bug Fix':   { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5' },
  UI:          { bg: '#fef3c7', fg: '#854d0e', border: '#fde047' },
  Backend:     { bg: '#ede9fe', fg: '#5b21b6', border: '#c4b5fd' },
};

export const CATEGORY_ORDER = ['Feature', 'Enhancement', 'Bug Fix', 'UI', 'Backend'];
